import { CHART_RANGE_STEPPER_BUTTON_WIDTH } from '@/components/common/charts/ChartRangeControls';
import styleguide from '@common/styleguide';
import { toPx } from '@common/util/styles';
import { isNumber } from '@common/util/types';
import {
    arrayFromTickRange,
    dependencyArrayFromTickRange,
    findValueNode,
    makeTick,
    nowTick,
    Tick,
    TickRange,
    ValueNode,
    ValueSeries,
} from '@common/valueSeries';
import { localPoint } from '@visx/event';
import {
    DefaultOutput,
    PickD3Scale,
    scaleBand,
    scaleLinear,
    scaleOrdinal,
    scalePoint,
} from '@visx/scale';
import { useCallback, useMemo, useState } from 'react';
import { defaultChartTooltipWidth } from './ChartTooltip';

export const DEFAULT_MARGIN = { top: 10, right: 10, bottom: 40, left: 60 };

export interface ChartPos {
    x: number;
    y: number;
}

export interface TooltipParams<Data extends Tick> {
    data: Data | null;
    visible: boolean;
    midpointX: number;
    midpointY: number;
    leftBound: number;
    rightBound: number;
    left: number;
}

export type TooltipCursorEventHandler = (
    event:
        | React.TouchEvent<SVGRectElement>
        | React.MouseEvent<SVGRectElement>
        | { x: number; y: number }
) => void;

export interface TooltipInfo<Data extends Tick> {
    tooltip: TooltipParams<Data>;
    handleCursor: TooltipCursorEventHandler;
    handleCursorLeave: () => void;
    handleTooltipMouseEnter: () => void;
}

export interface MarginParams {
    left: number;
    right: number;
    top: number;
    bottom: number;
}

export type ScaleTickRange =
    | PickD3Scale<'band', DefaultOutput, string>
    | PickD3Scale<'point', DefaultOutput, string>;

export interface ScaleTickRangeInput {
    outputRange: [number, number];
    inputRange: TickRange;
    type?: 'band' | 'point';
}

export const makeScaleTickRange = ({
    outputRange,
    inputRange,
    type = 'point',
}: ScaleTickRangeInput): ScaleTickRange => {
    const domain = arrayFromTickRange(inputRange).map((v) => v.date);
    return type === 'band'
        ? scaleBand<string>({ range: outputRange, domain, padding: 0 })
        : scalePoint({ range: outputRange, domain });
};

export type ScaleNumeric = PickD3Scale<'linear', number>;

export const scaleNumeric = ({
    values,
    height,
    padDomain = 0.2,
    shouldClamp = true,
}: {
    values: (number | null)[];
    height: number;
    padDomain?: number;
    shouldClamp?: boolean;
}): ScaleNumeric => {
    const min = Math.min(...values.filter(isNumber));
    const max = Math.max(...values.filter(isNumber));

    // Clamp min & max to ensure domains with negative values are shown correctly.
    // If all positive: show minimum at 0.
    // If any negative: show maximum as max of (domain max, absolute value of min)
    // Ensures padding is applied to both min and max when non-zero.
    const newMin = shouldClamp ? Math.min(0, min) : min;
    const newMax = shouldClamp ? Math.max(Math.abs(newMin), max) : max;

    const range = Math.abs(newMax - newMin);
    const padding = range * padDomain;

    // Don't pad min if all positive - results in a showing a negative number at the bottom of the y-axis
    const minWithPadding =
        newMin - padding < 0 && min >= 0 ? 0 : newMin - padding;
    const maxWithPadding = newMax + padding;

    return scaleLinear<number>({
        domain: [minWithPadding, maxWithPadding],
        range: [height, 0],
    });
};

export type ScaleColor = PickD3Scale<'ordinal', string, string>;

export const useColorScale = (domain: string[]): ScaleColor => {
    return useMemo(
        () =>
            scaleOrdinal({
                domain,
                range: chartTheme.colors.chart,
            }),
        [domain]
    );
};

export interface ChartTheme {
    fontSize: string;
    colors: {
        gridline: string;
        label: string;
        chart: string[];
    };
    zIndex: {
        rangeMenu: number;
        rangeMenuButton: number;
        rangeStepper: number;
        tooltip: number;
        todayLabel: number;
    };
}

const chartColors = [
    '#8F99F1',
    '#C5DFFB',
    '#F18F8F',
    '#9DE8ED',
    '#A0E89F',
    '#FFE49F',
    '#F5C3A4',
    '#E9A7E2',
];

export const chartTheme: ChartTheme = {
    fontSize: toPx(styleguide.typography.fontSizes.xs),
    colors: {
        gridline: '#EBEBEB',
        label: styleguide.semantic.textGrayOnLight,
        chart: chartColors,
    },
    zIndex: {
        tooltip: 300,
        rangeMenu: 220,
        rangeMenuButton: 200,
        rangeStepper: 199,
        todayLabel: 190,
    },
};

export const useTimeSeriesEntityData = <
    VN extends ValueNode,
    Datum extends Tick,
>(
    range: TickRange,
    valueNodes: ValueNode<VN>[],
    datumFromValueNode: (vn: VN) => Datum | undefined
): {
    data: Datum[];
    recordedData: Datum[];
    forecastData: Datum[];
} => {
    const todayTickDate = nowTick(range.unit).date;

    return useMemo(() => {
        const data: Datum[] = [];
        const recordedData: Datum[] = [];
        const forecastData: Datum[] = [];

        for (const valueNode of valueNodes) {
            const datum = datumFromValueNode(valueNode);
            if (!datum) continue;
            if (datum.date >= todayTickDate) forecastData.push(datum);
            if (datum.date <= todayTickDate) recordedData.push(datum);
            data.push(datum);
        }

        return { data, recordedData, forecastData };
    }, [valueNodes, datumFromValueNode, todayTickDate]);
};

export const useTickDatumGetterForDate = <
    VN extends ValueNode,
    Datum extends Tick,
>(
    valueSeries: ValueSeries<VN>,
    datumFromValueNode: (vn: VN) => Datum | undefined
): ((date: string) => Datum | undefined) => {
    return useCallback(
        (date: string): Datum | undefined => {
            const vn = getValueNodeForDate(valueSeries, date);
            if (!vn) return;
            return datumFromValueNode(vn) ?? undefined;
        },
        [valueSeries, datumFromValueNode]
    );
};

export const getValueNodeForDate = <VN extends ValueNode>(
    valueSeries: ValueSeries<VN>,
    date: string
): VN | undefined => {
    const tick = makeTick(date, valueSeries.unit);
    try {
        return findValueNode(valueSeries, tick);
    } catch {
        return;
    }
};

// Invert the xScale and return a memoized date getter for x points
const useDateFromX = ({
    xScale,
    margin,
}: {
    xScale: ScaleTickRange;
    margin: MarginParams;
}): ((x: number) => string | undefined) => {
    return useMemo(() => {
        const domain = xScale.domain();
        const paddingOuter = xScale(domain[0]);
        const eachBand = xScale.step();

        return (x: number): string | undefined => {
            const index = Math.round(
                (x - margin.left - (paddingOuter || 0)) / eachBand
            );
            return xScale.domain()[index];
        };
    }, [xScale, margin]);
};

export const useXScaleForTickRange = ({
    width,
    range,
    type,
}: {
    width: number;
    range: TickRange;
    type?: ScaleTickRangeInput['type'];
}) => {
    return useMemo(
        () =>
            makeScaleTickRange({
                outputRange: [0, width],
                inputRange: range,
                type,
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [...dependencyArrayFromTickRange(range), width, type]
    );
};

export const useYScaleNumeric = ({
    domain,
    height,
    padDomain,
    shouldClamp,
}: {
    domain: (number | null)[];
    height: number;
    padDomain?: number;
    shouldClamp?: boolean;
}): ScaleNumeric =>
    useMemo(
        () =>
            scaleNumeric({
                values: domain,
                height,
                padDomain,
                shouldClamp,
            }),
        [domain, height, padDomain, shouldClamp]
    );

export const useTickRangeTooltipInfo = <Data extends Tick>({
    xScale,
    yScale,
    maxValueForDate,
    tooltipDataForDate,
    innerWidth,
    margin,
    tooltipWidth = defaultChartTooltipWidth,
    xBuffer = 20,
}: {
    xScale: ScaleTickRange;
    yScale: ScaleNumeric;
    maxValueForDate: (date: string) => number | null | undefined;
    tooltipDataForDate: (date: string) => Data | null | undefined;
    innerWidth: number;
    margin: MarginParams;
    tooltipWidth?: number;
    xBuffer?: number;
}): TooltipInfo<Data> => {
    const dateFromX = useDateFromX({ xScale, margin });

    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [tooltipData, setTooltipData] = useState<Data | null>(null);
    const [tooltipPos, setTooltipPos] = useState<ChartPos>({ x: 0, y: 0 });

    const handleCursor: TooltipCursorEventHandler = (event) => {
        const coords = 'x' in event ? event : localPoint(event);
        if (!coords) return;

        const { x } = coords;
        const date = dateFromX(x);
        if (!date) return;

        const x0 = xScale(date);
        if (!isNumber(x0)) return;
        const yVal = maxValueForDate(date);
        if (!isNumber(yVal)) return;
        const y0 = yScale(yVal);
        const data = tooltipDataForDate(date);
        if (!data) return;

        setTooltipData(data);
        setTooltipPos({ x: x0, y: y0 });
        setTooltipOpen(true);
    };

    const handleCursorLeave = () => {
        setTooltipOpen(false);
    };

    const handleTooltipMouseEnter = () => {
        setTooltipOpen(true);
    };

    // When the tooltip is visible:
    // - Clamp horizontally to ensure tooltip stays within chart boundary
    // - Add slight overflow to left bound to ensure the tooltip is always
    //   hoverable on first tick
    // - Ensure that the tooltip doesn't block the chart range stepper buttons
    const tooltipLeftBound = margin.left + CHART_RANGE_STEPPER_BUTTON_WIDTH * 4;
    const tooltipRightBound =
        innerWidth +
        margin.left -
        tooltipWidth -
        CHART_RANGE_STEPPER_BUTTON_WIDTH * 4;

    const switchToLeft = tooltipPos.x + xBuffer / 2 > tooltipRightBound;

    let tooltipLeft = switchToLeft
        ? tooltipPos.x - tooltipWidth
        : tooltipPos.x + xBuffer;

    tooltipLeft = Math.max(tooltipLeft, tooltipLeftBound);
    tooltipLeft = Math.min(tooltipLeft, tooltipRightBound);

    const tooltip = {
        data: tooltipData,
        visible: tooltipOpen,
        midpointX: tooltipPos.x,
        midpointY: tooltipPos.y,
        leftBound: tooltipLeftBound,
        rightBound: tooltipRightBound,
        left: tooltipLeft,
    };

    return {
        tooltip,
        handleCursor,
        handleCursorLeave,
        handleTooltipMouseEnter,
    };
};
